import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import type { Database } from '@/integrations/supabase/types';
import { logger } from '@/utils/logger';

type OperadoraDoadora = Database['public']['Enums']['operadora_doadora_enum'];

interface UpdateItemPedidoData {
  itemId: string;
  updates: {
    // Campos de identificação
    numero_simulacao_operadora?: string | null;
    numero_pedido_operadora?: string | null;
    ext_ref?: string | null;
    
    // Dados do plano/produto
    plano_nome?: string | null;
    plano_valor?: number | null;
    franquia_gb?: number | null;
    aparelho?: string | null;
    cobertura?: string | null;
    
    // Linha telefônica (campos legados mantidos para retrocompatibilidade)
    ddd?: string | null;
    linha?: string | null;
    operadora_doadora?: OperadoraDoadora | null;
    tipo_chip?: string | null;
    
    // Múltiplas linhas telefônicas (novo formato)
    linhas_telefone?: Array<{
      ddd: string;
      linha: string;
      operadora_doadora?: string | null;
      tipo_chip?: string | null;
      // 🆕 Campos de aparelho
      aparelho_valor?: string | null;
      aparelho_modelo?: string | null;
      aparelho_parcelas?: string | null;
    }>;
    
    // Datas e agendamentos
    mes_trabalho?: string | null;
    data_ativacao?: string | null;
    data_cancelamento?: string | null;
    data_agendamento?: string | null;
    agenda_tipo?: 'PORTABILIDADE' | 'INSTALACAO' | null;
    agenda_data?: string | null;
    agenda_observacoes?: string | null;
    
    // Responsáveis
    gestor_id?: string | null;
    cessionario_id?: string | null;
    responsavel_tipo?: string | null;
    
    // Endereço
    endereco_instalacao?: any;
    
    // Valores
    quantidade?: number;
    valor_unit?: number;
    
    // Observações
    observacoes_item?: string | null;
    
    // Campos dinâmicos adicionais
    [key: string]: any;
  };
}

export function useUpdateItemPedido() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ itemId, updates }: UpdateItemPedidoData) => {
      // 🔴 VALIDAÇÃO CRÍTICA: Só exigir tipo_chip se houver DDD ou Linha preenchidos
      if ('tipo_chip' in updates) {
        const tipoChip = updates.tipo_chip;
        const temDDD = updates.ddd && updates.ddd.trim() !== '';
        const temLinha = updates.linha && updates.linha.trim() !== '';
        
        // ✅ Se tem DDD ou Linha preenchidos, tipo_chip é obrigatório
        if ((temDDD || temLinha) && (!tipoChip || tipoChip === '' || tipoChip === null)) {
          logger.error('❌ [CRÍTICO] Bloqueando salvamento de tipo_chip vazio com linha preenchida:', {
            itemId,
            updates,
            timestamp: new Date().toISOString()
          });
          
          throw new Error('Tipo de Chip é obrigatório e não pode ser vazio. Selecione E-SIM, Chip Físico ou Ambos.');
        }
        
        // ✅ Se linha está vazia, permitir salvar tipo_chip vazio também
        if (!temDDD && !temLinha) {
          logger.info('ℹ️ [VALIDAÇÃO] Linha vazia, permitindo tipo_chip vazio');
        } else if (tipoChip) {
          logger.info('✅ [VALIDAÇÃO] Tipo de Chip válido:', tipoChip);
        }
      }

      // 🔍 LOG DETALHADO: Registrar toda tentativa de salvamento
      logger.log('💾 [ANTES SALVAMENTO] Dados que serão salvos:', {
        itemId: itemId.substring(0, 8) + '...',
        campos_atualizados: Object.keys(updates),
        gestor_id: updates.gestor_id || 'não alterado',
        tipo_chip: updates.tipo_chip || 'não alterado',
        operadora_doadora: updates.operadora_doadora || 'não alterado',
        ddd: updates.ddd || 'não alterado',
        linha: updates.linha || 'não alterado',
        updates_completo: updates,
        timestamp: new Date().toISOString()
      });

      // ✅ VALIDAÇÃO EXTRA: Verificar se linhas_telefone tem DDDs vazios
      if (updates.linhas_telefone) {
        const linhasComDDDVazio = updates.linhas_telefone.filter(
          (l: any) => !l.ddd || l.ddd === ''
        );
        
        if (linhasComDDDVazio.length > 0) {
          logger.warn('⚠️ [VALIDAÇÃO] Detectadas linhas com DDD vazio:', {
            total_linhas: updates.linhas_telefone.length,
            linhas_com_ddd_vazio: linhasComDDDVazio.length,
            linhas_afetadas: linhasComDDDVazio,
            timestamp: new Date().toISOString()
          });
        }
        
        // 🆕 AUTO-GERAR OBSERVAÇÕES PARA APARELHOS
        const linhasComAparelho = updates.linhas_telefone.filter(
          (l: any) => l.tipo_chip === 'aparelho' && (l.aparelho_modelo || l.aparelho_valor || l.aparelho_parcelas)
        );
        
        if (linhasComAparelho.length > 0) {
          const aparelhosTexto = linhasComAparelho.map((l: any) => {
            const modelo = l.aparelho_modelo || 'N/I';
            const valor = l.aparelho_valor ? `R$ ${Number(l.aparelho_valor).toLocaleString('pt-BR')}` : 'N/I';
            const parcelas = l.aparelho_parcelas ? `${l.aparelho_parcelas}x` : 'à vista';
            return `APARELHO: ${modelo} - ${valor} em ${parcelas}`;
          }).join('\n');
          
          // Concatenar nas observações existentes ou criar nova
          const obsExistentes = updates.observacoes_item || '';
          const obsLimpas = obsExistentes.replace(/APARELHO:.*$/gm, '').trim();
          updates.observacoes_item = obsLimpas ? `${obsLimpas}\n${aparelhosTexto}` : aparelhosTexto;
          
          logger.info('📱 [APARELHO] Observações auto-geradas:', {
            linhas_com_aparelho: linhasComAparelho.length,
            observacoes_geradas: aparelhosTexto,
            timestamp: new Date().toISOString()
          });
        }
      }

      const { data, error } = await supabase
        .from('itens_pedido')
        .update(updates as any) // Cast necessário para campos dinâmicos
        .eq('id', itemId)
        .select()
        .single();

      if (error) {
        logger.error('❌ [ERRO CRÍTICO SUPABASE] Falha ao salvar:', {
          error,
          errorMessage: error.message,
          errorDetails: error.details,
          errorHint: error.hint,
          errorCode: error.code,
          itemId: itemId.substring(0, 8) + '...',
          updates
        });
        throw error;
      }

      logger.log('✅ [APÓS SALVAMENTO] Dados salvos no banco:', {
        itemId: itemId.substring(0, 8) + '...',
        gestor_id_salvo: data.gestor_id,
        gestor_id_esperado: updates.gestor_id || 'não alterado',
        gestor_match: data.gestor_id === (updates.gestor_id || data.gestor_id),
        tipo_chip_salvo: data.tipo_chip,
        data_completa: data,
        timestamp: new Date().toISOString()
      });

      return data;
    },
    onSuccess: (data, variables) => {
      logger.log('🎉 [onSuccess] Salvamento bem-sucedido:', {
        itemId: variables.itemId.substring(0, 8) + '...',
        camposSalvos: Object.keys(variables.updates),
        gestor_id_update: variables.updates.gestor_id,
        gestor_id_retornado: data.gestor_id,
        timestamp: new Date().toISOString()
      });
      
      // Invalidar queries relacionadas ao item específico
      queryClient.invalidateQueries({ queryKey: ['item-completo', variables.itemId] });
      queryClient.invalidateQueries({ queryKey: ['itens_pedido', variables.itemId] });
      
      // Invalidar queries do pedido pai
      queryClient.invalidateQueries({ queryKey: ['pedido-itens-completo', data.pedido_id] });
      queryClient.invalidateQueries({ queryKey: ['pedido', data.pedido_id] });
      
      // Invalidar queries de totais e resumo (CRÍTICO para atualizar Venda Nova e Total)
      queryClient.invalidateQueries({ queryKey: ['pedido_totais', data.pedido_id] });
      queryClient.invalidateQueries({ queryKey: ['pedido_resumo', data.pedido_id] });
      queryClient.invalidateQueries({ queryKey: ['pedido-detail', data.pedido_id] });
      
      // Invalidar views agregadas
      queryClient.invalidateQueries({ queryKey: ['v_planilha_mae'] });
      queryClient.invalidateQueries({ queryKey: ['planilha-itens'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['kanban'], exact: false });
      
      logger.log('✅ [onSuccess] Todas as queries invalidadas');
      
      // Toast removido - será exibido apenas após confirmação visual no componente
    },
    onError: (error: Error) => {
      logger.error('❌ [ERRO] Falha ao atualizar item:', {
        error,
        message: error.message,
        stack: error.stack,
        timestamp: new Date().toISOString()
      });
      toast.error(`Erro ao atualizar item: ${error.message}`);
    },
  });
}
